import React, { useMemo } from 'react';
import { Link, Outlet, useOutletContext } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPAButton from '../../shared/SPAButton';
import SPADataTable from '../../shared/SPADataTable';
import SPAPill from '../../shared/SPAPill';
import SPASectionHeading from '../../shared/SPASectionHeading';
import type { EditableProgram } from '../ProgramEdit/useProgram';
import { panelClass } from '../../shared/formPanel';
import ResourceActions from './ResourceActions';
import ResourceIconTile from './ResourceIconTile';
import {
  ProgramResource, ResourceOwner, useProgramResources, useReorderResources,
} from './useProgramResources';

const columnHelper = createColumnHelper<ProgramResource>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline tw-transition-colors';

const TitleCell = ({ resource }: { resource: ProgramResource }) => (
  <span className="tw-flex tw-items-center tw-gap-3">
    {resource.icon && <ResourceIconTile icon={resource.icon} />}
    <Link className={linkClass} to={`${resource.id}/edit`}>{resource.title}</Link>
  </span>
);

// Drag-to-reorder needs the data order, so nothing sorts. The title opens the edit dialog, as the
// other content tables' titles open their rows; categories with a teacher-page icon show it.
const getColumns = (owner: ResourceOwner): ColumnDef<ProgramResource, unknown>[] => [
  columnHelper.accessor('title', {
    cell: info => <TitleCell resource={info.row.original} />,
    enableSorting: false,
    header: 'Title',
    meta: { width: 'auto' },
  }),
  columnHelper.accessor('name', { enableSorting: false, header: 'Category' }),
  columnHelper.accessor('input_type', { enableSorting: false, header: 'Type', size: 80 }),
  columnHelper.accessor('published', {
    cell: info => (
      <SPAPill tone={info.getValue() ? 'success' : 'neutral'}>{info.getValue() ? 'Published' : 'Unpublished'}</SPAPill>
    ),
    enableSorting: false,
    header: 'Status',
    size: 120,
  }),
  columnHelper.display({
    cell: info => <ResourceActions owner={owner} resource={info.row.original} />,
    enableSorting: false,
    header: 'Actions',
    id: 'actions',
    meta: { width: 1 },
  }),
];

const resourceRowId = (resource: ProgramResource) => resource.id;
const resourceRowLabel = (resource: ProgramResource) => resource.title;

interface ProgramResourcesPanelProps {
  owner: ResourceOwner;
  title: string;
}

// AIDEV-NOTE: The SPA's resources section: the legacy jQuery resources tool (sortable table,
// modal form, rails-ujs actions) rebuilt on SPADataTable and routed dialogs against
// PlatoApi::ModelResourcesController, for whichever model owns the resources (a program, a unit).
// The edit pages mount it as a section route, with the add and edit dialogs as its child routes,
// which read the owner from the Outlet context.
export const ProgramResourcesPanel = ({ owner, title }: ProgramResourcesPanelProps) => {
  const resources = useProgramResources(owner);
  const reorderResources = useReorderResources(owner);
  const columns = useMemo(() => getColumns(owner), [owner]);

  return (
    <div className={panelClass}>
      <SPASectionHeading
        className="tw-mb-4"
        meta={(
          <SPAButton asChild size="xs" variant="primary">
            <Link to="new">Add Resource</Link>
          </SPAButton>
        )}
        title={title}
      />
      {resources.isError ? (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
          Couldn’t load the resources. Reload the page to try again.
        </p>
      ) : (
        <SPADataTable
          columns={columns}
          data={resources.data ?? []}
          defaultSort={[]}
          emptyMessage="No resources yet."
          isLoading={resources.isLoading}
          loadingRows={3}
          reorder={{
            getRowId: resourceRowId, getRowLabel: resourceRowLabel, onReorder: reorderResources,
          }}
        />
      )}
      <Outlet context={owner} />
    </div>
  );
};

// The program edit page's section: the program from its Outlet context is the owner.
const ProgramResourcesSection = () => {
  const program = useOutletContext<EditableProgram>();
  const owner = useMemo<ResourceOwner>(
    () => ({ modelClass: 'Program', modelId: program.id, programId: program.id }),
    [program.id],
  );

  return <ProgramResourcesPanel owner={owner} title="Program Resources" />;
};

export default ProgramResourcesSection;
